import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

// eslint-disable-next-line no-restricted-imports
import { alpha, Box, TableBody as MuiTableBody, TableBodyProps, TableRow, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { LoadingIcon } from "@shared/Loading";
import { Column, Row, Table } from "@tanstack/react-table";

import { PrimaryButton, SecondaryButton } from "../CustomButton";

import { MinimalTableCell } from "./MinimalTableComponents";

const useStyles = makeStyles()((theme: Theme) => ({
    tbodyRelative: {
        position: "relative",
    },
    loadingOverlay: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: alpha(theme.palette.grey[300], 0.5),
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        zIndex: 1000,
    },
    noDataContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
    },
}));

type RenderArgs<T> = {
    rows: Row<T>[];
    columns: Column<T>[];
};

type Props<T> = Omit<TableBodyProps, "children"> & {
    table: Table<T>;
    loading?: boolean;
    noDataMessage: string;
    children: ReactNode | ((args: RenderArgs<T>) => ReactNode);
    filter?: {
        isActive: boolean;
        onResetFilter: () => void;
    };
    error?: {
        isError: boolean;
        onError: () => void;
        message?: string;
    };
};

export const TableBody = <T,>({
    loading = false,
    table,
    noDataMessage,
    children,
    error,
    filter,
    className,
    ...domProps
}: Props<T>) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);
    const rows = table.getRowModel().rows;
    const columns = table.getAllColumns();
    const content = typeof children === "function" ? children({ rows, columns }) : children;

    const countAfterFilter = table.getFilteredRowModel().rows.length;

    return (
        <MuiTableBody {...domProps} className={clsx(classes.tbodyRelative, className)}>
            {loading ? (
                <Box component="tr" className={classes.loadingOverlay} aria-busy="true" aria-live="polite">
                    <td>
                        <LoadingIcon size="block" />
                    </td>
                </Box>
            ) : null}

            {!error?.isError && !filter?.isActive && !countAfterFilter ? (
                <TableRow>
                    <MinimalTableCell colSpan={table.getAllLeafColumns().length}>
                        <Box className={classes.noDataContainer}>{noDataMessage}</Box>
                    </MinimalTableCell>
                </TableRow>
            ) : null}

            {!error?.isError && filter?.isActive && !countAfterFilter && filter?.onResetFilter ? (
                <TableRow>
                    <MinimalTableCell colSpan={table.getAllLeafColumns().length}>
                        <Box className={classes.noDataContainer}>
                            {t("common:table.noFilterResults")}
                            <PrimaryButton onClick={filter.onResetFilter} disabled={loading}>
                                {t("common:button.resetFilter")}
                            </PrimaryButton>
                        </Box>
                    </MinimalTableCell>
                </TableRow>
            ) : null}

            {error?.isError ? (
                <TableRow>
                    <MinimalTableCell colSpan={table.getAllLeafColumns().length}>
                        <Typography color="error" gutterBottom>
                            {error.message || t("common:error.technicalDifficulties.message")}
                        </Typography>
                        <SecondaryButton onClick={error.onError}>
                            {t("common:error.technicalDifficulties.refreshButton")}
                        </SecondaryButton>
                    </MinimalTableCell>
                </TableRow>
            ) : null}

            {!error?.isError ? content : null}
        </MuiTableBody>
    );
};
